import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from '../i18n/translations';
import flagEn from '../i18n/flags/en.webp';
import flagPtBR from '../i18n/flags/pt-BR.webp';
import flagEs from '../i18n/flags/es.webp';

const LanguageContext = createContext(null);

// English is always the default and the fallback — never auto-detected from
// the browser/IP, per product requirement. The visitor must pick another
// language explicitly, and that choice is then remembered locally.
// `flag` is a small compressed image (see frontend/src/i18n/flags/), not an
// emoji — emoji flags render as plain two-letter text on Windows Chrome/Edge,
// which has no flag glyphs installed by default. All three source images are
// cropped to the exact same pixel dimensions so they never look distorted or
// mismatched in a row.
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', shortLabel: 'EN', flag: flagEn },
  { code: 'pt-BR', label: 'Português', shortLabel: 'PT-BR', flag: flagPtBR },
  { code: 'es', label: 'Español', shortLabel: 'ES', flag: flagEs },
];

const STORAGE_KEY = 'fte_language';
export const DEFAULT_LANGUAGE = 'en';

const isSupported = (code) => SUPPORTED_LANGUAGES.some((l) => l.code === code);

const readInitialLanguage = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && isSupported(stored)) return stored;
  } catch (e) {
    // localStorage unavailable (private mode, disabled storage) — fall back silently
  }
  return DEFAULT_LANGUAGE;
};

const getByPath = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);

const interpolate = (str, vars) => {
  if (!vars) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_, key) => (vars[key] !== undefined ? String(vars[key]) : `{{${key}}}`));
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((code) => {
    if (!isSupported(code)) return;
    setLanguageState(code);
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch (e) {
      // ignore persistence failures — language still applies for this session
    }
  }, []);

  // Translate a static UI-chrome string by dot-path key, e.g. t('header.login').
  // Falls back to the English dictionary, then to the key itself, so a missing
  // translation never renders blank/undefined to a visitor.
  const t = useCallback(
    (key, vars) => {
      const primary = getByPath(translations[language], key);
      if (typeof primary === 'string') return interpolate(primary, vars);
      const fallback = getByPath(translations[DEFAULT_LANGUAGE], key);
      if (typeof fallback === 'string') return interpolate(fallback, vars);
      return key;
    },
    [language]
  );

  // Same fallback contract as t(), for static repeater lists (feature cards, steps,
  // program cards) whose CMS-editable defaults are arrays of objects, not single
  // strings — t()'s string-only check can't return these.
  const tList = useCallback(
    (key) => {
      const primary = getByPath(translations[language], key);
      if (Array.isArray(primary)) return primary;
      const fallback = getByPath(translations[DEFAULT_LANGUAGE], key);
      if (Array.isArray(fallback)) return fallback;
      return [];
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, tList, languages: SUPPORTED_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
};

export default LanguageContext;
